/**
 * React Query wrapper around `fetchMeetingRoomBusyEvents`.
 *
 * Exposes three hooks so room busy behaves just like interviewer busy:
 *
 *   - `useMeetingRoomBusyEventsQuery` — single-week live fetch for the
 *     currently visible anchor. Returns the result + a blocking flag
 *     the calendar uses to draw its loader overlay.
 *   - `useMeetingRoomBusyPrefetch` — silently warms the next N weeks
 *     via `useQueries` so forward navigation is a cache hit.
 *   - `buildMeetingRoomBusyQueryKey` — shared key helper; both hooks
 *     use it so the single-week live query and a prefetched chunk
 *     collapse onto the same cache row.
 */
import { useEffect } from 'react';
import {
  useQueries,
  useQuery,
  type UseQueryOptions,
  type UseQueryResult,
} from '@tanstack/react-query';
import moment from 'moment-timezone';
import { normalizeTimeZoneForAtsCalendarApi } from '@/api/calendarEvents';
import {
  fetchMeetingRoomBusyEvents,
  type FetchMeetingRoomBusyArgs,
} from './fetchMeetingRoomBusyEvents';
import type { MeetingRoomBusyFetchResult } from './types';

export interface UseMeetingRoomBusyEventsQueryArgs extends FetchMeetingRoomBusyArgs {
  enabled?: boolean;
}

export function buildMeetingRoomBusyQueryKey(
  sortedEmails: string[],
  fromIso: string,
  toIso: string,
  timezone: string,
  atsJobId: number | null
): readonly unknown[] {
  return [
    'meeting-room-busy',
    fromIso,
    toIso,
    normalizeTimeZoneForAtsCalendarApi(timezone),
    atsJobId,
    sortedEmails.join('|'),
  ] as const;
}

function sanitizeEmails(emails: string[]): string[] {
  return [...emails]
    .map((e) => e?.trim())
    .filter((e): e is string => Boolean(e))
    .sort();
}

export function useMeetingRoomBusyEventsQuery({
  roomEmails,
  fromIso,
  toIso,
  timezone,
  atsJobId,
  enabled = true,
}: UseMeetingRoomBusyEventsQueryArgs): UseQueryResult<
  MeetingRoomBusyFetchResult,
  Error
> {
  const sortedEmails = sanitizeEmails(roomEmails);

  const isEnabled =
    Boolean(enabled) &&
    sortedEmails.length > 0 &&
    Boolean(fromIso) &&
    Boolean(toIso) &&
    timezone !== 'local';

  const query = useQuery({
    queryKey: buildMeetingRoomBusyQueryKey(
      sortedEmails,
      fromIso,
      toIso,
      timezone,
      atsJobId ?? null
    ),
    queryFn: () =>
      fetchMeetingRoomBusyEvents({
        roomEmails: sortedEmails,
        fromIso,
        toIso,
        timezone,
        ...(atsJobId !== undefined && atsJobId > 0 ? { atsJobId } : {}),
      }),
    enabled: isEnabled,
    staleTime: 30_000,
  });

  useEffect(() => {
    if (query.isError && query.error) {
      console.warn('[meeting-room-busy] error', query.error);
    }
  }, [query.isError, query.error]);

  return query;
}

export interface UseMeetingRoomBusyPrefetchArgs {
  /** Visible anchor week start ISO — skipped (the live query owns it). */
  anchorWeekStartIso: string;
  timezone: string;
  roomEmails: string[];
  atsJobId?: number;
  /** Total weeks to cover incl. anchor. Default 3 (matches interviewer). */
  prefetchWeeks?: number;
  /** Extra quiet weeks after `prefetchWeeks`. Default 1. */
  lookaheadPrefetchWeeks?: number;
  enabled?: boolean;
}

/**
 * Silently warms `useQueries` for weeks ahead of the visible anchor so
 * navigation is instant. Mirrors `useInterviewerSchedulePrefetch`.
 */
export function useMeetingRoomBusyPrefetch({
  anchorWeekStartIso,
  timezone,
  roomEmails,
  atsJobId,
  prefetchWeeks = 3,
  lookaheadPrefetchWeeks = 1,
  enabled = true,
}: UseMeetingRoomBusyPrefetchArgs): void {
  const sortedEmails = sanitizeEmails(roomEmails);
  const tz = normalizeTimeZoneForAtsCalendarApi(timezone);
  const totalWeeks = Math.max(0, prefetchWeeks + lookaheadPrefetchWeeks);

  type Chunk = { fromIso: string; toIso: string };
  const chunks: Chunk[] = [];
  for (let offset = 1; offset < totalWeeks; offset += 1) {
    const weekStart = moment(anchorWeekStartIso)
      .tz(tz)
      .startOf('day')
      .add(offset, 'weeks');
    const weekEnd = weekStart.clone().add(7, 'days');
    chunks.push({
      fromIso: weekStart.toISOString(),
      toIso: weekEnd.toISOString(),
    });
  }

  const isActive =
    Boolean(enabled) &&
    sortedEmails.length > 0 &&
    Boolean(anchorWeekStartIso) &&
    timezone !== 'local' &&
    chunks.length > 0;

  const queries: UseQueryOptions<MeetingRoomBusyFetchResult, Error>[] =
    chunks.map((c) => ({
      queryKey: buildMeetingRoomBusyQueryKey(
        sortedEmails,
        c.fromIso,
        c.toIso,
        timezone,
        atsJobId ?? null
      ),
      queryFn: () =>
        fetchMeetingRoomBusyEvents({
          roomEmails: sortedEmails,
          fromIso: c.fromIso,
          toIso: c.toIso,
          timezone,
          ...(atsJobId !== undefined && atsJobId > 0 ? { atsJobId } : {}),
        }),
      enabled: isActive,
      staleTime: 30_000,
    }));

  useQueries({ queries });
}
